import { MESSAGE_SHOP_NAME_TAG_KEY_PREFIX } from 'components/flowBuilder/constants';
import {
  LegacyTagDelimiters,
  SHOP_NAME_TAG_KEY,
} from 'components/flowBuilder/constants/tags';

const LEGACY_SHOP_NAME_TAG_KEY = `${LegacyTagDelimiters.START}shop_name${LegacyTagDelimiters.END}`;

export const messageContentIncludesShopNameTag = (
  content?: string | null,
): boolean => {
  return (
    !!content &&
    (content.includes(SHOP_NAME_TAG_KEY) ||
      content.includes(LEGACY_SHOP_NAME_TAG_KEY))
  );
};

/**
 * Returns true when the message satisfies the shop-name requirement — either by
 * including the shop name merge tag ({{shop.name}} / {shop_name}) or by spelling
 * out the merchant's actual shop name as plain text (case-insensitive).
 */
export const messageContentIncludesShopName = (
  content?: string | null,
  shopName?: string | null,
): boolean => {
  if (messageContentIncludesShopNameTag(content)) return true;

  const normalizedShopName = shopName?.trim().toLowerCase();
  if (!content || !normalizedShopName) return false;

  return content.toLowerCase().includes(normalizedShopName);
};

export const prefixMessageContentWithShopNameTagIfMissing = (
  content: string,
): string => {
  return messageContentIncludesShopNameTag(content)
    ? content
    : `${MESSAGE_SHOP_NAME_TAG_KEY_PREFIX}${content}`;
};

export const REPLY_TO_SKIP = 'Reply SKIP to skip your next order.';

export type ReplyToSkipEdit =
  | { action: 'add'; text: string }
  | { action: 'remove'; text: string }
  | { action: 'none' };

/**
 * Decides how the reply-to-skip toolbar toggle should edit the message body
 * (FBP-3695). Enabling appends the "Reply SKIP…" instruction to the end (unless
 * it is already present); disabling removes it. Mirrors the Reply STOP control.
 */
export const getReplyToSkipEdit = (
  content: string,
  nextEnabled: boolean,
): ReplyToSkipEdit => {
  if (!nextEnabled) return { action: 'remove', text: REPLY_TO_SKIP };
  return content.includes(REPLY_TO_SKIP)
    ? { action: 'none' }
    : { action: 'add', text: `\n\n${REPLY_TO_SKIP}` };
};

/**
 * Removes the auto-inserted "Reply SKIP…" instruction from a message body.
 * Used at submit time to keep the body in sync when reply-to-skip is being
 * force-disabled on a now-ineligible flow, so we never send subscribers an
 * instruction the backend won't honor. Matches the editor's own removeText
 * behavior (strip the phrase, then trim trailing whitespace).
 */
export const stripReplyToSkipPhrase = (content: string): string =>
  content.replaceAll(REPLY_TO_SKIP, '').trim();
